

















































































































































import {
  Vue,
  Component,
  Watch
} from "@/shared/vuepack";
import * as _ from "lodash";
import {
  CommunicationDeliveryType,
  CommunicationDeliveryTypeMap,
  ContactInformation,
  FetchState, NotificationPreference, NotificationPreferenceUpdateRequest, Preference,
  PreferenceConfiguration,
} from "@/shared/types";
import * as RouteNames from "@/employee/route-names";
import SettingsNavigation from "@/employee/components/settings/SettingsNavigation.vue";
import SettingsHeader from "@/employee/components/settings/SettingsHeader.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useContactInfoStore } from "@/shared/store/contactinfo";
import { useThemeStore } from "@/shared/store/theme";
import { useNotificationPreferencesStore } from "@/shared/store/notificationPreferences";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useNotifierStore } from "@/shared/store/notifier";

const initialState: ContactInformation = {
  firstName: "",
  middleInitial: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  mobilePhoneNumber: "",
  isMailingSameAsPhysical: false,
  dateOfBirth: "",
  mailingAddress: {
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: ""
  },
  physicalAddress: {
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: ""
  },
  lastFourSsn: "",
  memberNumber: "",
  lastLogin: "",
  username: ""
};

@Component({
  computed: {
    CommunicationDeliveryTypeMap () {
      return CommunicationDeliveryTypeMap;
    }
  },
  components: {
    SettingsNavigation,
    SettingsHeader,
    GenericLoadingSpinner,
  }
})
export default class UserNotificationSettings extends Vue {
  RouteNames = RouteNames;
  notificationPreferencesStore = useNotificationPreferencesStore();
  themeStore = useThemeStore();
  contactInfoStore = useContactInfoStore();
  featureFlagStore = useFeatureFlagStore();
  notifierStore = useNotifierStore();
  formModel: ContactInformation = { ...initialState };
  updatedPreferences: NotificationPreference[] = [];
  preferenceMap = new Map<string, Map<string, boolean>>();
  isOptedIntoPaper = false;
  debounceSaveNotificationPreferences = _.debounce(this.saveNotificationPreferences, 1000);
  disablePaperlessToggle = false;

  mounted () {
    this.contactInfoStore.getContactInformation();
    this.initializeFromStateValue();
    this.notificationPreferencesStore.getNotificationPreferences();
  }

  @Watch("notificationPreferencesStore.notificationPreferencesLoaded", {immediate: true})
  onNotificationPreferencesLoaded () {
    if (this.notificationPreferencesStore.notificationPreferencesLoaded) {
      this.initializePreferenceState();
    }
  }

  @Watch("notificationPreferencesStore.hasSavedNotificationPreferences", {immediate: true})
  onHasSavedNotificationPreferences () {
    if (this.notificationPreferencesStore.hasSavedNotificationPreferences) {
      this.initializePreferenceState();
      this.disablePaperlessToggle = false;
    }
  }

  initializePreferenceState () {
    this.createPreferenceMap();
    this.isOptedIntoPaper = this.HasAnyPaperPreferences;
  }

  get isLoaded () {
    return this.contactInfoStore.contactInformationState === FetchState.Loaded;
  }

  get isNotificationPreferencesLoaded () {
    return this.notificationPreferencesStore.notificationPreferencesLoaded;
  }

  get isNotificationPreferencesLoading () {
    return this.notificationPreferencesStore.notificationPreferencesLoading;
  }

  get showNotificationPreferences () {
    return this.isNotificationPreferencesLoaded &&
        this.notificationPreferencesStore.notificationPreferences.length > 0;
  }

  get notificationPreferences (): NotificationPreference[] | [] {
    return this.notificationPreferencesStore.notificationPreferences;
  }

  get HasAnyPaperPreferences (): boolean {
    for (const notificationPreference of this.notificationPreferences) {
      for (const preference of notificationPreference.preferences) {
        if (preference.configurations.find(x =>
            x.communicationDeliveryType === CommunicationDeliveryType.Paper &&
            x.optedIn === true &&
            x.enabled === true)) {
          return true;
        }
      }
    }
    return false;
  }

  onTogglePaperlessCommunications () {
    if (this.isOptedIntoPaper === true) {
      this.disablePaperlessToggle = true;
      this.addPaperlessNotificationPreferences();
      this.saveNotificationPreferences();
    }
  }

  addPaperlessNotificationPreferences () {
    this.debounceSaveNotificationPreferences.cancel();

    const clonedNotificationPreferences = _.cloneDeep(this.notificationPreferences);

    for (const notificationPreference of clonedNotificationPreferences) {
      let changedPreference: Preference;
      for (const [prefIndex, preference] of notificationPreference.preferences.entries()) {
        changedPreference = preference;
        for (const configuration of preference.configurations) {
          if (configuration.communicationDeliveryType === CommunicationDeliveryType.Paper &&
              configuration.optedIn === true &&
              configuration.enabled === true) {
            configuration.optedIn = false;
            this.addChangedNotificationPreferences(notificationPreference, changedPreference, CommunicationDeliveryType.Paper);
            const matchingPreference = notificationPreference.preferences[prefIndex];
            for (const matchingConfiguration of matchingPreference.configurations) {
              if (matchingConfiguration.communicationDeliveryType === CommunicationDeliveryType.Email &&
                  matchingConfiguration.optedIn === false &&
                  matchingConfiguration.enabled === true) {
                matchingConfiguration.optedIn = true;
                this.addChangedNotificationPreferences(notificationPreference, changedPreference, CommunicationDeliveryType.Email);
              }
            }
          }
        }
      }
    }
  }

  onToggleConfiguration (notification: NotificationPreference, preference: Preference, communicationDeliveryType: string, newValue: boolean) {
    if (!notification && !preference && !preference.configurations && !this.isStringNullOrEmpty(communicationDeliveryType)) {
      return;
    }

    const config = this.getConfig(preference.configurations, communicationDeliveryType);
    if (!config) {
      return;
    }
    config.optedIn = newValue;

    const updatedNotification = {
      ...notification,
      preferences: notification.preferences.slice()
    };

    if (this.wasPreferenceChanged(notification.categoryDescription, preference)) {
      this.addUpdatedPreference(preference, updatedNotification, communicationDeliveryType);
    } else {
      this.removeExistingUpdatedNotifications(notification, preference, communicationDeliveryType);
    }
    this.debounceSaveNotificationPreferences();
  }

  getConfig (configurations: PreferenceConfiguration[], communicationDeliveryType: string): PreferenceConfiguration | null {
    if (configurations && configurations.length > 0) {
      return configurations.find(c => c.communicationDeliveryType === communicationDeliveryType);
    }
    return null;
  }

  createPreferenceMap () {
    this.preferenceMap.clear();
    for (const notificationPreference of this.notificationPreferencesStore.notificationPreferences) {
      for (const preference of notificationPreference.preferences) {
        const key = this.keyForPreference(notificationPreference.categoryDescription, preference.name);
        const map = new Map<string, boolean>();
        for (const config of preference.configurations) {
          map.set(config.communicationDeliveryType, config.optedIn);
        }
        this.preferenceMap.set(key, map);
      }
    }
  }

  keyForPreference (categoryDescription: string, preferenceName: string) {
    return `${categoryDescription}::${preferenceName}`.toLowerCase();
  }

  wasPreferenceChanged (categoryDescription: string, preference: Preference) {
    if (preference && !this.isStringNullOrEmpty(categoryDescription)) {
      const key = this.keyForPreference(categoryDescription, preference.name);
      const baseline = this.preferenceMap.get(key);
      if (!baseline) {
        return false;
      }
      for (const config of preference.configurations) {
        const deliveryType = baseline.get(config.communicationDeliveryType);
        if (deliveryType !== undefined && deliveryType !== null && deliveryType !== config.optedIn) {
          return true;
        }
      }
    }
    return false;
  }

  addUpdatedPreference (preference: Preference, notification: NotificationPreference, communicationDeliveryType: string) {
    if (preference && !this.isStringNullOrEmpty(notification.categoryDescription)) {
      this.addChangedNotificationPreferences(notification, preference, communicationDeliveryType);
    }
  }

  addChangedNotificationPreferences (notification: NotificationPreference, preference: Preference, communicationDeliveryType: string) {
    const changedNotifications = notification.preferences
        .filter(x => x.name === preference.name)
        .map(x => ({
          ...x,
          configurations: (x.configurations || []).filter(
              y => y.communicationDeliveryType === communicationDeliveryType
          )
        }))
        .filter(x => x.configurations.length > 0);

    if (changedNotifications.length === 0) {
      return;
    }

    const existingNotification = this.updatedPreferences.find(n =>
        n.categoryDescription === notification.categoryDescription
    );

    if (!existingNotification) {
      this.updatedPreferences.push({
        category: notification.category,
        categoryDescription: notification.categoryDescription,
        preferences: changedNotifications
      });
    } else {
      const existingPrefIndex = existingNotification.preferences.findIndex(
          p => p.name === preference.name
      );

      if (existingPrefIndex === -1) {
        existingNotification.preferences.push(changedNotifications[0]);
      } else {
        const targetPref = existingNotification.preferences[existingPrefIndex];
        const incomingConfigs = changedNotifications[0].configurations;

        const kept = (targetPref.configurations || []).filter(x => x.communicationDeliveryType !== communicationDeliveryType);
        targetPref.configurations = kept.concat(incomingConfigs);
      }
    }
  }

  isStringNullOrEmpty (str) {
    return str === undefined || str === null || str === "";
  }

  getPreferenceMap (notification: NotificationPreference, preference: Preference) {
    if (notification && preference) {
      const key = this.keyForPreference(notification.categoryDescription, preference.name);
      return this.preferenceMap.get(key);
    }
    return null;
  }

  getCurrentOptedInValue (preference: Preference, communicationDeliveryType: string) {
    if (preference && !this.isStringNullOrEmpty(communicationDeliveryType)) {
      const config = (preference.configurations || [])
          .find(c => c.communicationDeliveryType === communicationDeliveryType);
      if (!config) {
        return undefined;
      }
      return config.optedIn;
    }
    return undefined;
  }

  findCategoryIndex (notification: NotificationPreference): number {
    if (notification) {
      return this.updatedPreferences.findIndex(x => {
        return x.categoryDescription === notification.categoryDescription;
      });
    }
    return -1;
  }

  findPreferenceIndex (notification: NotificationPreference, preference: Preference): number {
    if (notification && preference) {
      return notification.preferences.findIndex(x => {
        return x.name === preference.name;
      });
    }
    return -1;
  }

  removeExistingUpdatedNotifications (notification: NotificationPreference, preference: Preference, communicationDeliveryType: string) {
    if (!notification || !preference
        || this.isStringNullOrEmpty(notification.categoryDescription
            || this.isStringNullOrEmpty(communicationDeliveryType))) {
      return;
    }

    const preferenceMap = this.getPreferenceMap(notification, preference);
    if (!preferenceMap) {
      return;
    }

    const current = this.getCurrentOptedInValue(preference, communicationDeliveryType);
    const base = preferenceMap.get(communicationDeliveryType);
    if (base === undefined || current !== base) {
      return;
    }

    const catIndex = this.findCategoryIndex(notification);
    if (catIndex === -1) {
      return;
    }

    const existingNotification = this.updatedPreferences[catIndex];
    const prefIndex = this.findPreferenceIndex(existingNotification, preference);
    if (prefIndex === -1) {
      return;
    }

    const pref = existingNotification.preferences[prefIndex];
    const configIndex = pref.configurations.findIndex(x => {
      return x.communicationDeliveryType === communicationDeliveryType;
    });

    if (configIndex !== -1) {
      pref.configurations.splice(configIndex, 1);
    }

    if (pref.configurations.length === 0) {
      existingNotification.preferences.splice(prefIndex, 1);
    }

    if (existingNotification.preferences.length === 0) {
      this.updatedPreferences.splice(catIndex, 1);
    }
  }

  async saveNotificationPreferences () {
    if (this.updatedPreferences.length === 0) {
      return;
    }
    const request: NotificationPreferenceUpdateRequest = {
      notificationPreferences: this.updatedPreferences
    };
    await this.notificationPreferencesStore.updateNotificationPreferences(request);
    this.createPreferenceMap();
    this.updatedPreferences = [];
  }

  cloneToCurrentModel (o) {
    return _.cloneDeep(o);
  }

  @Watch("contactInfoStore.contactInformationState")
  initializeFromStateValue () {
    const contactInformation = this.contactInfoStore.contactInformation;
    if (this.contactInfoStore.contactInformationState === FetchState.Loaded && !contactInformation) {
      this.formModel = this.cloneToCurrentModel(initialState);
    } else if (contactInformation) {
      this.formModel = this.cloneToCurrentModel(contactInformation);
    }
  }

  @Watch("contactInfoStore.contactInformation")
  onPlanStateChanged () {
    this.initializeFromStateValue();
  }
  get showUndeliverableAddressWarning () {
    return this.formModel.mailingAddress.isUndeliverable === true;
  }
  get privacyUrl () {
    return "https://hsabank.com/HSABank/About/Security/Privacy-and-Opt-Out-Notice";
  }

}
